import React from 'react'
import styled, { css } from 'styled-components'
import { darken } from 'polished'
import { Link as HistoryLink, Link } from 'react-router-dom'

import { ArrowLeft } from 'react-feather'
import { RowBetween } from '../Row'
import QuestionHelper from '../QuestionHelper'

/** Contenedor redondeado clásico (import pool / add-remove). */
const RoundedTabsContainer = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  border-radius: 3rem;
  justify-content: space-evenly;
`

/** Tres columnas iguales a los lados para centrar Swap|Pool ópticamente con acciones al extremo derecho. */
const SwapPoolTabsBar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid ${({ theme }) => theme.bg3};
`

const SwapPoolTabsBarLead = styled.span`
  min-width: 0;
`

const SwapPoolTabsLinks = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  justify-content: center;
  gap: 1.75rem;
`

const SwapPoolTabsBarEnd = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-width: 0;

  & > div {
    margin-left: 0;
  }
`

/**
 * Reserva el mismo hueco que `Settings compact` en la pestaña Swap; invisible
 * para que al ir a Pool la cabecera conserve altura y el centro no salte.
 */
export const SwapPoolTabsTrailingReserve = styled.span.attrs({ 'aria-hidden': true })`
  display: inline-block;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  visibility: hidden;
  pointer-events: none;
`

const SwapPoolNavLink = styled(Link)<{ $active?: boolean }>`
  align-items: center;
  justify-content: center;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  color: ${({ theme }) => theme.text3};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 0.4rem 0.25rem;
  border-bottom: 2px solid transparent;
  margin-bottom: -0.7rem;

  ${({ $active, theme }) =>
    $active &&
    css`
      font-weight: 700;
      color: ${theme.primary1};
      border-bottom-color: ${theme.primary1};
    `}

  :hover {
    color: ${({ theme }) => darken(0.08, theme.text1)};
  }

  :focus-visible {
    color: ${({ theme }) => darken(0.08, theme.text1)};
  }
`

const ActiveText = styled.div`
  font-weight: 500;
  font-size: 20px;
`

const StyledArrowLeft = styled(ArrowLeft)`
  color: ${({ theme }) => theme.text1};
`

export function SwapPoolTabs({
  active,
  trailing
}: {
  active: 'swap' | 'pool'
  /** P. ej. Transaction Settings en la misma fila que las pestañas, alineado a la derecha. */
  trailing?: React.ReactNode
}) {
  return (
    <SwapPoolTabsBar>
      <SwapPoolTabsBarLead aria-hidden />
      <SwapPoolTabsLinks>
        <SwapPoolNavLink id={`swap-nav-link`} to={'/swap'} $active={active === 'swap'}>
          Swap
        </SwapPoolNavLink>
        <SwapPoolNavLink id={`pool-nav-link`} to={'/pool'} $active={active === 'pool'}>
          Pool
        </SwapPoolNavLink>
      </SwapPoolTabsLinks>
      <SwapPoolTabsBarEnd>{trailing}</SwapPoolTabsBarEnd>
    </SwapPoolTabsBar>
  )
}

export function ImportPoolTabs() {
  return (
    <RoundedTabsContainer>
      <RowBetween style={{ padding: '1rem' }}>
        <HistoryLink to="/pool">
          <StyledArrowLeft />
        </HistoryLink>
        <ActiveText>Import Pool</ActiveText>
        <QuestionHelper text={"Use this tool to import pairs that don't automatically appear in the interface."} />
      </RowBetween>
    </RoundedTabsContainer>
  )
}

export function AddRemoveTabs({ adding }: { adding: boolean }) {
  return (
    <RoundedTabsContainer>
      <RowBetween style={{ padding: '1rem' }}>
        <HistoryLink to="/pool">
          <StyledArrowLeft />
        </HistoryLink>
        <ActiveText>{adding ? 'Add' : 'Remove'} Liquidity</ActiveText>
        <QuestionHelper
          text={
            adding
              ? 'When you add liquidity, you are given pool tokens representing your position. These tokens automatically earn fees proportional to your share of the pool, and can be redeemed at any time.'
              : 'Removing pool tokens converts your position back into underlying tokens at the current rate, proportional to your share of the pool. Accrued fees are included in the amounts you receive.'
          }
        />
      </RowBetween>
    </RoundedTabsContainer>
  )
}
